import React, { useRef } from "react";
import * as XLSX from "xlsx-js-style";
import i18next from "i18next";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { fetchData } from "../../helpers/fetchData";
import SharedButton from "../../components/Shared/SharedButton";
import { useSelector } from "react-redux";

const ExportInvoices = ({
  filters,
  columnsData,
  apiUrl,
  exportOptionsFormat,
  name,
}) => {
  const { t } = useTranslation();
  const iframeRef = useRef(null);
  const menuRef = useRef(null);
  const lang = i18next.language;
  const { user } = useSelector((state) => state.auth);

  const handlePrint = async () => {
    let payload = { ...filters };
    if (payload?.customer_id && payload.customer_id.value !== undefined)
      payload.customer_id = payload.customer_id.value;
    if (payload?.company_id && payload.company_id.value !== undefined)
      payload.company_id = payload.company_id.value;
    if (payload?.branch_id && payload.branch_id.value !== undefined)
      payload.branch_id = payload.branch_id.value;
    if (!payload?.customer_id) payload.customer_id = 0;
    const response = await fetchData("POST", apiUrl, payload);
    if (response?.data) {
      const tableContent = generateTable(response.data);
      printTable(tableContent);
    }
  };

  const generateTable = (dataRaw) => {
    // Some endpoints may return wrapped data (e.g., { data: [...] } or { payload: [...] })
    const data = Array.isArray(dataRaw)
      ? dataRaw
      : Array.isArray(dataRaw?.data)
        ? dataRaw.data
        : Array.isArray(dataRaw?.payload)
          ? dataRaw.payload
          : [];

    const thStyle =
      "border: 1px solid #d1d5db; padding: 6px 8px; text-align: center; font-weight: 600; font-size: 15px; background-color: #d1d5db;";
    const tdStyle =
      "border: 1px solid #d1d5db; padding: 5px 8px; text-align: center; font-size: 14px;";

    let html = `<div style="border: 1px solid #d1d5db; border-radius: 4px; overflow: hidden; margin-top: 10px;">`;
    html += `<table style="width:100%; border-collapse:collapse; font-family:sans-serif; font-size:14px; border-style: hidden;">`;
    html += `<thead><tr>`;
    columnsData.forEach((col) => {
      html += `<th style="${thStyle}">${col.header}</th>`;
    });
    html += `</tr></thead><tbody>`;

    if (data.length === 0) {
      html += `<tr><td colspan="${columnsData.length}" style="${tdStyle} text-align:center; color:#6b7280;">-</td></tr>`;
    } else {
      data.forEach((row, index) => {
        try {
          const rowBg = index % 2 === 0 ? "#fff" : "#f9fafb";
          html += `<tr style="background:${rowBg};">`;
          columnsData.forEach((col) => {
            let defaultValue = col?.type == Number ? 0 : "";
            let value =
              col.key == "no" ? index + 1 : row[col.key] || defaultValue;
            if (col.key == "total_company_amount") {
              value = (row?.company_share || 0) + (row?.company_tax || 0);
            }
            if (col.key == "grass_amount") {
              value = (row?.discount_amount || 0) + (row?.taxable_amount || 0);
            }
            if (col.key == "status" || col.key == "paid") {
              const s = row?.status !== undefined ? row?.status : row?.paid;
              if (s === "Paid" || s === "paid" || s === 1 || s === true) {
                value = t("Paid");
              } else if (s === "cancel" || s === "Cancel") {
                value = t("Cancel");
              } else {
                value = t("Unpaid"); // handles "Unpaid", falsy, 0, null, etc.
              }
            }
            html += `<td style="${tdStyle}">${value}${col?.type == Number ? " " + t("SAR") : ""}</td>`;
          });
          html += `</tr>`;
        } catch (err) {
          console.error("Error generating row", err);
        }
      });
    }

    html += `</tbody></table></div>`;
    return html;
  };

  const printTable = (tableContent) => {
    const direction = lang === "ar" ? "rtl" : "ltr";
    const iframe = iframeRef.current;
    const doc = iframe.contentWindow.document;

    const businessNameEn = user?.Business?.name_en || "";
    const businessNameAr = user?.Business?.name_ar || "";
    const businessAddress = user?.Business?.address || "";
    const userEmail = user?.email || "";
    const userMobile = user?.mobile_number || "";
    const businessLogo = user?.Business?.logo || user?.Business?.logo_url;

    doc.open();
    doc.write(
      `<html><head><title>${name || "Invoices"}</title><style>@media print {@page {size: A3 landscape; margin: 10mm;} body {margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; font-family: 'Inter', sans-serif; direction: ${direction};} header, footer {display: none;} tr {page-break-inside: avoid;}}</style></head><body style="font-family:sans-serif; direction:${direction}">`,
    );

    // 3-Column Business Header
    doc.write(`
    <div style="display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 20px; border-bottom: 2px solid #ccc; padding-bottom: 10px;">
      
      <!-- Column 1: Contact Info -->
      <div style="flex: 1; text-align: start; font-size: 13px; color: #333; line-height: 1.6;">
        ${businessAddress ? `<div><span style="font-weight: bold;">${t("Address")}:</span> ${businessAddress}</div>` : ""}
        ${userMobile ? `<div><span style="font-weight: bold;">${t("Phone")}:</span> ${userMobile}</div>` : ""}
        ${userEmail ? `<div><span style="font-weight: bold;">${t("Email")}:</span> ${userEmail}</div>` : ""}
      </div>

      <!-- Column 2: Logo and Main Info (Center) -->
      <div style="flex: 1; text-align: center;">
        ${businessLogo ? `<img src="${businessLogo}" style="height: 75px; margin-bottom: 8px; object-fit: contain;" />` : ""}
        ${businessNameEn ? `<div style="font-weight: bold; font-size: 18px; color: #1f2937;">${businessNameEn}</div>` : ""}
        ${businessNameAr && businessNameAr !== businessNameEn ? `<div style="font-weight: bold; font-size: 18px; color: #1f2937;">${businessNameAr}</div>` : ""}
        <div style="font-weight: bold; font-size: 16px; color: #1f2937; margin-top: 8px;">${name ? t(name) : t("Invoices")}</div>
        ${
          filters?.start_date || filters?.end_date
            ? `<div style="font-size: 12px; color: #555; margin-top: 5px;">${t("From")}: ${filters?.start_date || ""} - ${t("To")}: ${filters?.end_date || ""}</div>`
            : ""
        }
      </div>

      <!-- Column 3: Contact Info (Mirrored) -->
      <div style="flex: 1; text-align: end; font-size: 13px; color: #333; line-height: 1.6;">
        ${businessAddress ? `<div><span style="font-weight: bold;">${t("Address")}:</span> ${businessAddress}</div>` : ""}
        ${userMobile ? `<div><span style="font-weight: bold;">${t("Phone")}:</span> ${userMobile}</div>` : ""}
        ${userEmail ? `<div><span style="font-weight: bold;">${t("Email")}:</span> ${userEmail}</div>` : ""}
      </div>
      
    </div>
    `);

    doc.write(tableContent);
    doc.write("</body></html>");
    doc.close();

    iframe.onload = () => {
      iframe.contentWindow.print();
    };
  };

  const handleExcelExport = async () => {
    let payload = { ...filters };
    if (payload?.customer_id && payload.customer_id.value !== undefined)
      payload.customer_id = payload.customer_id.value;
    if (payload?.company_id && payload.company_id.value !== undefined)
      payload.company_id = payload.company_id.value;
    if (payload?.branch_id && payload.branch_id.value !== undefined)
      payload.branch_id = payload.branch_id.value;
    if (!payload?.customer_id) payload.customer_id = 0;
    const response = await fetchData("POST", apiUrl, payload);

    if (!response?.data) {
      toast.error("No data available for export");
      return;
    }

    const exportData = exportOptionsFormat(response?.data);
    if (!exportData || exportData.length === 0) {
      toast.error("No data available for export");
      return;
    }

    const headers = Object.keys(exportData[0]);
    const reportTitle = name ? t(name) : t("Invoices");
    const dateRange =
      filters?.start_date || filters?.end_date
        ? `${t("From")}: ${filters?.start_date || ""}  -  ${t("To")}: ${filters?.end_date || ""}`
        : "";
    const businessNameEn = user?.Business?.name_en || "";
    const businessNameAr = user?.Business?.name_ar || "";
    const displayBusinessName =
      lang === "ar"
        ? businessNameAr || businessNameEn
        : businessNameEn || businessNameAr;

    const aoa = [
      [`${displayBusinessName}`],
      [`${reportTitle}`],
      [`${dateRange}`],
      [], 
      headers,
    ];

    exportData.forEach((row) => {
      aoa.push(headers.map((h) => row[h]));
    });

    const worksheet = XLSX.utils.aoa_to_sheet(aoa);

    for (let c = 0; c < headers.length; c++) {
      const cellRef = XLSX.utils.encode_cell({ c, r: 4 });
      if (worksheet[cellRef]) {
        worksheet[cellRef].s = {
          fill: { patternType: "solid", fgColor: { rgb: "D1D5DB" } },
          font: { bold: true },
          alignment: { horizontal: "center", vertical: "center" },
        };
      }
    }

    for (let r = 0; r < 3; r++) {
      const cellRef = XLSX.utils.encode_cell({ c: 0, r });
      if (worksheet[cellRef]) {
        worksheet[cellRef].s = {
          font: { bold: true, sz: r === 0 ? 16 : 14 },
          alignment: { horizontal: "center", vertical: "center" },
        };
      }
    }

    const columnWidths = headers.map(() => ({ wch: 20 }));
    worksheet["!cols"] = columnWidths;

    worksheet["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: headers.length - 1 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: headers.length - 1 } },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      reportTitle.substring(0, 31),
    );
    XLSX.writeFile(workbook, `${reportTitle}.xlsx`);
  };

  return (
    <div>
      <div className="flex items-center justify-end gap-2">
        <SharedButton
          title={t("Export Excel")}
          icon={"pi pi-file-excel"}
          iconSize={20}
          onClick={handleExcelExport}
        />
        <SharedButton
          title={t("Export PDF")}
          icon={"pi pi-print"}
          iconSize={20}
          onClick={handlePrint}
        />
      </div>
      <iframe ref={iframeRef} style={{ display: "none" }}></iframe>
    </div>
  );
};

export default ExportInvoices;
